import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { defaultValues, ExperimentStatus, sampleExperimentFile } from './constants';
import ExperimentFormTextInput from './form/ExperimentFormTextInput';
import ExperimentFormTextAreaInput from './form/ExperimentFormTextAreaInput';
import { useDispatch, useSelector } from 'react-redux';
import {
  createNewExperiment,
  getAllExperiments,
  updateExperiment
} from '../../actions/FieldAllocationAction';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import { Dispatch, SetStateAction } from 'react';
import LoaderIcon from '@cp/src/assets/icons/LoaderIcon';
import { RootState } from '@cp/src/store';
import { Button, Switch, Typography } from '@navi/web-ui/lib/primitives';
import styles from './index.module.scss';
import { AllocationExperimentFormValues } from './interfaces';
import { useForm } from 'react-hook-form';
import { setFailureReport } from '../../reducers/FieldAllocationSlice';

interface IAllocationExperimentFormProps {
  isDrawerOpen: boolean;
  onClose: () => void;
  isEditDisabled?: boolean;
  setIsSubmitButtonDisabled: Dispatch<SetStateAction<boolean>>;
  isEditPage?: boolean;
  setIsDirtyField?: Dispatch<SetStateAction<boolean>>;
  viewMode?: boolean;
  setIsDrawerOpen: (value: boolean) => void;
}

const AllocationExperimentForm = forwardRef((props: IAllocationExperimentFormProps, ref) => {
  const {
    onClose,
    isEditDisabled = false,
    setIsSubmitButtonDisabled,
    isEditPage = false,
    setIsDirtyField,
    isDrawerOpen,
    setIsDrawerOpen
  } = props;

  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty, errors, isValid },
    watch,
    setValue,
    getValues
  } = useForm<AllocationExperimentFormValues>({
    defaultValues,
    mode: 'onTouched',
    reValidateMode: 'onBlur'
  });

  const experimentData = useSelector((state: RootState) => state?.fieldAllocation?.experimentData);

  const [isExperimentActiveToggleDirty, setIsExperimentActiveToggleDirty] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (experimentData?.id) {
      reset({
        name: experimentData?.name,
        details: experimentData?.details,
        status: experimentData?.status
      });
    } else {
      reset(defaultValues);
      setIsExperimentActiveToggleDirty(false);
    }
    dispatch(setFailureReport(null));
  }, [isDrawerOpen, experimentData]);

  useEffect(() => {
    if (typeof setIsDirtyField === 'function') {
      setIsDirtyField(isDirty || isExperimentActiveToggleDirty);
    }
  }, [isDirty, isExperimentActiveToggleDirty]);

  const refreshAllExperimentsList = () => {
    dispatch(getAllExperiments());
    onClose();
  };

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      handleSubmit(onSubmitExperiment)();
    }
  }));

  const watchName = watch('name');
  const watchDetails = watch('details');
  const watchStatus = watch('status');

  useEffect(() => {
    const isCreateFormValid = !!(watchName && watchDetails);
    const isUpdateFormValid = !!(watchDetails && watchStatus);

    if (isEditPage) {
      setIsSubmitButtonDisabled(!isUpdateFormValid);
    } else {
      setIsSubmitButtonDisabled(!isCreateFormValid);
    }
  }, [watchName, watchDetails, watchStatus, isEditPage, setIsSubmitButtonDisabled]);

  const createSuccessCallback = () => {
    toast('Experiment created successfully', { type: 'success' });
    setIsDrawerOpen(false);
    setIsSubmitButtonDisabled(true);
    refreshAllExperimentsList();
  };

  const updateSuccessCallback = () => {
    toast('Experiment updated successfully', { type: 'success' });
    setIsDrawerOpen(false);
    setIsSubmitButtonDisabled(true);
    refreshAllExperimentsList();
  };

  const onSubmitExperiment = async (data: AllocationExperimentFormValues) => {
    try {
      setIsSubmitButtonDisabled(true);

      if (experimentData?.id) {
        const payload = {
          ...data
        };
        dispatch(updateExperiment(experimentData?.id, payload, updateSuccessCallback));
      } else {
        delete data.status;
        dispatch(createNewExperiment(data, createSuccessCallback));
      }
    } catch (err) {
      toast('An error occurred while processing the request. Please try again.', { type: 'error' });
    } finally {
      setIsSubmitButtonDisabled(false);
    }
  };

  const showLoader = useSelector((state: RootState) => state.fieldAllocation?.isLoading);

  const toggleAgentActive = () => {
    const currentStatus = getValues('status');
    const newActiveState = currentStatus !== ExperimentStatus.ACTIVE;
    setValue('status', newActiveState ? ExperimentStatus.ACTIVE : ExperimentStatus.INACTIVE);
    setIsExperimentActiveToggleDirty(true);
  };

  if (showLoader) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoaderIcon
          size={40}
          borderWidth={4}
          borderColor="var(--navi-color-gray-border)"
          fillColor="var(--navi-color-blue-base)"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col p-4">
      <form onSubmit={handleSubmit(onSubmitExperiment)}>
        {isEditPage ? (
          <>
            <div className="flex mb-5">
              <Typography variant="h3">{experimentData?.name}</Typography>
            </div>
            <div className={styles.switchContainer}>
              <Switch
                checked={watchStatus === ExperimentStatus.ACTIVE}
                onChange={toggleAgentActive}
                disabled={isEditDisabled}
              />
              {getValues('status') === ExperimentStatus.ACTIVE ? (
                <Typography variant="p3" color="var(--navi-color-gray-c2)">
                  Active
                </Typography>
              ) : (
                <Typography variant="p3" color="var(--navi-color-gray-c2)">
                  Inactive
                </Typography>
              )}
            </div>
          </>
        ) : (
          <div className="flex flex-col mb-5">
            <ExperimentFormTextInput
              control={control}
              name="name"
              errors={errors}
              rules={{
                required: true,
                minLength: {
                  value: 3,
                  message: 'Please enter a valid name'
                }
              }}
              inputLabel="Experiment name"
              placeholder="Enter name"
              validationMessage="This field is required"
              showRequiredIcon
              isEditDisabled={isEditDisabled}
            />
          </div>
        )}
        <div className="flex flex-col mb-5">
          <ExperimentFormTextAreaInput
            control={control}
            name="details"
            errors={errors}
            rules={{
              required: true,
              minLength: {
                value: 30,
                message: 'Please enter at least 30 characters'
              }
            }}
            inputLabel="Details"
            placeholder="Enter here"
            validationMessage="This field is required"
            showRequiredIcon
            isEditDisabled={isEditDisabled}
          />
        </div>
      </form>
    </div>
  );
});

export default AllocationExperimentForm;
